import { Card, Pagination, Table } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import "./MyProfile.css";
import { formatDate } from "../../util/helper";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeAmount = (value) => Number(value || 0);
const buildMatchKey = (date, description, amount) =>
  `${normalizeText(date)}__${normalizeText(description)}__${normalizeAmount(
    amount
  )}`;
const getDisplayValue = (value, fallback = "-") => {
  const normalized = normalizeText(value);
  return normalized && normalized !== "-" ? value : fallback;
};
const getDefaultStatus = (description) =>
  normalizeText(description).includes("unlock") ? "Success" : "-";
const getTransactionType = (description, status) => {
  const normalizedDescription = normalizeText(description);
  const normalizedStatus = normalizeText(status);

  if (
    normalizedDescription.includes("unlock") ||
    normalizedStatus === "failure" ||
    normalizedDescription.includes("failed")
  ) {
    return "DR";
  }

  return "CR";
};

const MyActivity = ({ pointsData, paymentData = [] }) => {

  const { isMobile } = useGetBreakpoints();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const activityData = useMemo(() => {
    const matchedPaymentKeys = new Set();

    const mappedPoints = pointsData.map((point) => {
      const pointKey = buildMatchKey(
        point.date,
        point.transactionname,
        point.pointsconsumed
      );
      const matchedPayment = paymentData.find((payment) => {
        const paymentKey = buildMatchKey(
          payment.transactiondate,
          payment.description,
          payment.transactionamount
        );

        return paymentKey === pointKey;
      });

      if (matchedPayment) {
        matchedPaymentKeys.add(
          buildMatchKey(
            matchedPayment.transactiondate,
            matchedPayment.description,
            matchedPayment.transactionamount
          )
        );
      }

      return {
        ...point,
        transactiontype: getTransactionType(
          point.transactionname,
          matchedPayment?.status || point.status
        ),
        hostPaymentid:
          getDisplayValue(matchedPayment?.hostPaymentid) !== "-"
            ? matchedPayment?.hostPaymentid
            : getDisplayValue(point.hostPaymentid),
        status:
          getDisplayValue(matchedPayment?.status, "") ||
          getDisplayValue(point.status, "") ||
          getDefaultStatus(point.transactionname),
      };
    });

    const paymentOnlyRows = paymentData
      .filter((payment) => {
        const paymentKey = buildMatchKey(
          payment.transactiondate,
          payment.description,
          payment.transactionamount
        );
        return !matchedPaymentKeys.has(paymentKey);
      })
      .map((payment, index) => ({
        key: `payment-only-${payment.key ?? index}`,
        date: payment.transactiondate,
        transactionname: payment.description || "Payment",
        transactiontype: getTransactionType(
          payment.description,
          payment.status
        ),
        pointsconsumed:
          payment.displayAmount ?? payment.transactionamount ?? 0,
        hostPaymentid: payment.hostPaymentid || "-",
        status: payment.status || "-",
      }));

    return [...mappedPoints, ...paymentOnlyRows].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [paymentData, pointsData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return activityData.slice(startIndex, endIndex);
  }, [activityData, currentPage, pageSize]);
  
  const columns = [
    {
      title: "Date",
      key: "date",
      dataIndex: 'date',
      render: (text) => formatDate(text),
    },
    {
      title: "Description",
      key: "transactionname",
      dataIndex:'transactionname',
    },
    {
      title: "Amount",
      key: "pointsconsumed",
      render: (text, record) => (
        <div>
          <img
            src={
              record.transactiontype === "CR"
                ? "/assets/images/positiveIcon.png"
                : "/assets/images/negativeIcon.png"
            }
            alt={
              record.pointsconsumed > 0
                ? "Positive points icon"
                : "Negative points icon"
            }
            style={{ marginRight: 8 }} // Optional: for spacing the icon from the number
          />
          <span
          style={{
            fontSize:"16px",
            fontWeight:"bold",
            fontFamily: "Inter", 
          }}>
          {record.transactiontype === "CR" ? "+" : "-"}INR {record.pointsconsumed}
          </span>
         
        </div>
      ),
    },
    {
      title: "HostPayment ID",
      key: "hostPaymentid",
      dataIndex: "hostPaymentid",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (value) => value || "Success",
    },
  ];

  const renderMobileView = useCallback(
    () =>
      paginatedData.map((point) => (
        <div key={point.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <img
            src={
              point.transactiontype === "CR"
                ? "/assets/images/positiveIcon.png"
                : "/assets/images/negativeIcon.png"
            }
            alt={point.transactiontype === "CR" ? "Positive points icon" : "Negative points icon"}
            style={{ width: '74px', height: '74px', marginRight: '20px' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
              {formatDate(point.date)}
            </div>

            <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>
              {point.transactionname}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {`${point.transactiontype === "CR" ? "+" : "-"}INR ${point.pointsconsumed}`}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              HostPayment ID: {point.hostPaymentid || "-"}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              Status: {point.status || "-"}
            </div>
          </div>
  
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#666',marginRight: '20px' }}>
            {`${point.pointsconsumed}`}
          </div>
        </div>
      )),
    [paginatedData]
  );

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  return (
    <Card style={{ borderRadius: "5px" }}>
      <div className="myexim-table-myactivity-title">Payment History</div>
      {isMobile ? (
        <>
          <div className="mobile-payment-list" >{renderMobileView()}</div>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={activityData.length}
            onChange={handlePageChange}
            showSizeChanger={true} 
            showQuickJumper={false}
            simple
            style={{ marginTop: 16, textAlign: "center" }}
          />
        </>
      ) : (
      <Table
        rowKey={"key"}
        className="gx-table-responsive rfq-table myexim-table"
        columns={columns}
        dataSource={activityData}
        pagination={{
          position: ["bottomRight"],
          pageSize: 5,
        }}
      />
      )}
    </Card>
  );
};

export default MyActivity;
